import { memo, useMemo } from "react";
import { ContentSectionCustomCard } from "../../types";
import { Grid, useBreakpoint } from "@chakra-ui/react";
import { StreamLinkCard } from "./StreamLinkCard";
import { CenterImageContainer } from "./CenterImageContainer";
import { useAccountData } from "../../hooks/useAccountData";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

// Center image type ID from the database
const CENTER_IMAGE_TYPE_ID = 19;

interface StreamLinksContainerProps {
  data: ContentSectionCustomCard[];
  layout: "list" | "grid" | "horizontal" | "calendar";
  size: "s" | "m";
}

export const StreamLinksContainer = memo(
  ({ data, layout, size }: StreamLinksContainerProps) => {
    const isHorizontal = layout === "horizontal";
    const breakpoint = useBreakpoint({ ssr: false });

    const isMobileBreakpoint = useMobileBreakpoint();

    // Check if any card has a center image
    const hasCenterImages = useMemo(
      () =>
        data.some((card) =>
          card.images.some((img) => img.image_type_id === CENTER_IMAGE_TYPE_ID),
        ),
      [data],
    );

    const columnCount = useMemo(() => {
      if (isHorizontal) {
        if (["xs", "sm"].includes(breakpoint)) return 1;
        else return 2;
      }
      if (["xs", "sm"].includes(breakpoint)) return 1;
      else if (["md", "lg"].includes(breakpoint)) return 2;
      else if (["xl", "2xl"].includes(breakpoint)) return 3;
      else return 2;
    }, [isHorizontal, breakpoint]);

    const gap = useMemo(() => {
      if (["xs", "sm", "md", "lg"].includes(breakpoint)) return "16px";
      else if (["xl", "2xl"].includes(breakpoint)) return "24px";
      else return "32px";
    }, [breakpoint]);

    const accountData = useAccountData();
    const channels = accountData?.home_tab_channels;

    // Use Map for O(1) lookups instead of O(n) find operations
    const streamMap = useMemo(() => {
      const map = new Map<string, string>();

      // Add originals channel
      const originalsChannel = accountData?.originals_channel?.streams[0];
      if (originalsChannel) {
        map.set(originalsChannel.id, "originals");
      }

      // Add all other channels
      channels?.forEach((channel) => {
        channel.streams.forEach((stream) => {
          map.set(stream.id, stream.slug);
        });
      });

      return map;
    }, [channels, accountData?.originals_channel]);

    const rowCount = Math.ceil(data.length / columnCount);

    // If center images are present, delegate to CenterImageContainer
    if (hasCenterImages) {
      return <CenterImageContainer data={data} size={size} />;
    }

    if (layout === "horizontal") {
      const dataToShow = data.slice(0, isMobileBreakpoint ? 1 : 2);

      return (
        <Grid
          templateRows={`repeat($1, 1fr)`}
          autoColumns="1fr"
          gridAutoFlow={columnCount == 1 ? undefined : "column"}
          gap={4}
          marginRight={isMobileBreakpoint ? "16px" : "38px"}
        >
          {dataToShow.map((item) => {
            const urlLink = item.cta_url ?? "";
            const ctaUrl = streamMap.get(urlLink);

            return (
              <StreamLinkCard
                key={item.id}
                card={item}
                link={urlLink.startsWith("_") ? `/channel/${ctaUrl}` : urlLink}
                size={size}
              />
            );
          })}
        </Grid>
      );
    }

    return (
      <Grid
        templateRows={`repeat(${rowCount}, 1fr)`}
        autoColumns="1fr"
        gridAutoFlow={columnCount == 1 ? undefined : "column"}
        gap={gap}
        marginRight="16px"
      >
        {data.map((item) => {
          const urlLink = item.cta_url ?? "";
          const ctaUrl = streamMap.get(urlLink);

          return (
            <StreamLinkCard
              key={item.id}
              card={item}
              link={urlLink.startsWith("_") ? `/channel/${ctaUrl}` : urlLink}
              size={size}
            />
          );
        })}
      </Grid>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if data, layout, or size actually change
    return (
      prevProps.data === nextProps.data &&
      prevProps.layout === nextProps.layout &&
      prevProps.size === nextProps.size
    );
  },
);
